"use client"

import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDownToLine, ArrowUpFromLine, CircleDot, Play, Square } from 'lucide-react'
import { useStore, selectOpenShift, shiftAge } from '@/lib/store'
import { agoText, clockText, useLang, useT } from '@/lib/i18n'
import { useNow } from '@/hooks/use-now'
import { PHONE_QUERY, useMediaQuery } from '@/hooks/use-media-query'
import { BottomSheet, BottomSheetContent } from '@/components/ui/bottom-sheet'
import { shiftPreview } from '@/lib/cash'
import { cn } from '@/lib/utils'
import type { CashDirection, Shift } from '@/lib/mock-data'
import { OpenShiftDialog } from './open-shift-dialog'
import { CloseShiftDialog } from './close-shift-dialog'
import { CashMovementDialog } from './cash-movement-dialog'

/** One big tile of the shift card: money out (rose) or money in (green). */
function MoneyTile({ icon: Icon, tone, label, sub, onClick }: {
  icon: React.ElementType
  tone: 'out' | 'in'
  label: string
  sub: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'press min-h-24 rounded-[18px] border p-3.5 text-left flex flex-col justify-between gap-2 transition-colors duration-150',
        tone === 'out'
          ? 'border-rose-300/60 dark:border-rose-400/25 bg-rose-500/[0.07] dark:bg-rose-500/10 hover:bg-rose-500/[0.12] dark:hover:bg-rose-500/15'
          : 'border-emerald-300/60 dark:border-emerald-400/25 bg-emerald-500/[0.07] dark:bg-emerald-500/10 hover:bg-emerald-500/[0.12] dark:hover:bg-emerald-500/15',
      )}
    >
      <Icon className={cn('size-5.5', tone === 'out' ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400')} strokeWidth={2.2} />
      <span className="flex flex-col gap-0.5">
        <span className="text-[15px] font-black text-foreground leading-tight">{label}</span>
        <span className="text-[12px] text-muted-foreground leading-snug">{sub}</span>
      </span>
    </button>
  )
}

/**
 * 1.41 — the shift card (the owner chose „ა2" on the mock-ups, 2026-10-14:
 * „ძალიან ბანალურია" was the old three-row list). Who and since when, the
 * shift's own figures — receipts, the cash the drawer should hold, card — the
 * two money tiles in their meaning colours, and the close in the app's own
 * accent (he did not want amber there; red would echo „გატანა").
 */
function ShiftCard({ shift, stale, ago, onOut, onIn, onClose }: {
  shift: Shift
  stale: boolean
  ago: string
  onOut: () => void
  onIn: () => void
  onClose: () => void
}) {
  const t = useT()
  const lang = useLang()
  const now = useNow(30_000)
  const sales = useStore((s) => s.sales)
  const customerPayments = useStore((s) => s.customerPayments)
  const supplierPayments = useStore((s) => s.supplierPayments)
  const cashMovements = useStore((s) => s.cashMovements)
  const prepaidMoves = useStore((s) => s.prepaidMoves)   // 176 — cards / deposits in cash are in the drawer
  // the figures need the history (the close dialog waits for it too)
  const historyReady = useStore((s) => s.historyReady)
  const p = useMemo(
    () => shiftPreview(shift, { sales, customerPayments, supplierPayments, cashMovements, prepaidMoves }),
    [shift, sales, customerPayments, supplierPayments, cashMovements, prepaidMoves],
  )
  const mins = Math.max(0, Math.floor((now.getTime() - new Date(shift.opened_at).getTime()) / 60_000))
  const dur = mins >= 60 ? t('shift.durHM', { h: Math.floor(mins / 60), m: mins % 60 }) : t('shift.durM', { m: mins })
  const name = shift.cashier_name ?? '—'
  const money = (v: number) => (historyReady ? `₾${v.toFixed(2)}` : '…')

  return (
    <div className="flex flex-col">
      <div className="px-4 pt-4 pb-3 flex items-center gap-3">
        <span className="size-11 rounded-[14px] bg-muted text-foreground text-lg font-black flex items-center justify-center shrink-0 uppercase">
          {name.trim().charAt(0) || '?'}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-base font-black text-foreground truncate">{name}</p>
          <p className={cn('text-[12px] flex items-center gap-1.5', stale ? 'text-amber-700 dark:text-amber-400 font-semibold' : 'text-muted-foreground')}>
            <span className={cn('size-1.5 rounded-full shrink-0', stale ? 'bg-amber-500' : 'bg-emerald-500')} />
            {stale ? t('shift.staleSub', { ago }) : t('shift.sinceDur', { time: clockText(lang, shift.opened_at), dur })}
          </p>
        </div>
      </div>

      <div className="mx-3.5 p-3 rounded-[18px] bg-muted/70 dark:bg-white/[0.04] grid grid-cols-3 gap-2">
        <div className="min-w-0">
          <p className="text-[12px] text-muted-foreground truncate">{t('shift.statReceipts')}</p>
          <p className="text-lg font-black text-foreground tabular-nums">{historyReady ? p.count : '…'}</p>
        </div>
        <div className="min-w-0">
          <p className="text-[12px] text-muted-foreground truncate">{t('shift.statCash')}</p>
          <p className="text-lg font-black text-emerald-600 dark:text-emerald-400 tabular-nums truncate">{money(p.expected)}</p>
        </div>
        <div className="min-w-0">
          <p className="text-[12px] text-muted-foreground truncate">{t('shift.statCard')}</p>
          <p className="text-lg font-black text-foreground tabular-nums truncate">{money(p.card)}</p>
        </div>
      </div>

      <div className="p-3.5 grid grid-cols-2 gap-2.5">
        <MoneyTile icon={ArrowUpFromLine} tone="out" label={t('shift.tileOut')} sub={t('shift.cashOutSub')} onClick={onOut} />
        <MoneyTile icon={ArrowDownToLine} tone="in" label={t('shift.tileIn')} sub={t('reason.change')} onClick={onIn} />
      </div>

      <div className="px-3.5 pb-4">
        <button
          type="button"
          role="menuitem"
          onClick={onClose}
          className="press w-full h-14 rounded-[18px] bg-primary text-primary-foreground flex items-center justify-center gap-2.5 font-black text-[15px] hover:opacity-95 transition-opacity"
        >
          <Square className="size-4.5" strokeWidth={2.4} />
          <span>{t('shift.close')}</span>
          <span className="text-[12px] font-semibold opacity-80">· {t('shift.closeShort')}</span>
        </button>
      </div>
    </div>
  )
}

export function ShiftControl() {
  const t = useT()
  const currentShift = useStore(selectOpenShift)
  const [openDialog, setOpenDialog] = useState(false)
  const [closeDialog, setCloseDialog] = useState(false)
  // 107: the pill opens a menu — money in, money out, close — instead of
  // going straight to the close. A cashier paying a courier should not have
  // to open a dialog called „ცვლის დახურვა" to find where to write it.
  const [menuOpen, setMenuOpen] = useState(false)
  const [moveDir, setMoveDir] = useState<CashDirection | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  // 1.41 — a phone gets the card as a sheet from the bottom (a thumb's reach):
  // the app's BottomSheet, a Base UI dialog — focus kept inside, Escape, a tap
  // outside, the focus back on the pill (Codex)
  const isPhone = useMediaQuery(PHONE_QUERY)
  // Five minutes is plenty against a 36-hour threshold.
  const now = useNow(5 * 60_000)
  const age = currentShift ? shiftAge(currentShift.opened_at, now) : null

  // The POS banner asks for this dialog instead of owning one — see
  // `closeShiftAsked` in the store. Only a CHANGE opens it, never the value a
  // remount starts with. It still goes straight to the close, past the menu.
  const asked = useStore((s) => s.closeShiftAsked)
  const seen = useRef(asked)
  useEffect(() => {
    if (asked !== seen.current && currentShift) setCloseDialog(true)
    seen.current = asked
  }, [asked, currentShift])

  // Click outside or Escape closes the desktop menu — the same as the branch
  // switcher. The phone's sheet is a dialog that does this itself (and lives
  // in a portal, which this check would call „outside").
  useEffect(() => {
    if (!menuOpen || isPhone) return
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen, isPhone])

  // A shift closed elsewhere while the menu was open.
  useEffect(() => { if (!currentShift) setMenuOpen(false) }, [currentShift])

  const ago = age ? agoText(t, age.days) : ''
  const card = currentShift && (
    <ShiftCard
      shift={currentShift}
      stale={!!age?.stale}
      ago={ago}
      onOut={() => { setMenuOpen(false); setMoveDir('out') }}
      onIn={() => { setMenuOpen(false); setMoveDir('in') }}
      onClose={() => { setMenuOpen(false); setCloseDialog(true) }}
    />
  )

  return (
    <>
      {currentShift ? (
        <div className="relative" ref={menuRef}>
          {/* Phones: an icon-only circle, like the closed state below already
              was — the word „ღია" cost the header title its last letters
              („ბუღალტერ…", 2026-09-11). Green + pulse reads as open on its own.
              A STALE shift turns amber and says its age instead: amber is what
              closing a shift means everywhere else in the app. */}
          <button
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title={age?.stale ? t('shift.staleTitle', { ago }) : t('shift.menuTitle')}
            aria-label={age?.stale ? t('shift.staleAria', { ago }) : t('shift.openAria')}
            className={cn(
              'press flex items-center justify-center gap-2 h-9 w-9 sm:w-auto px-0 sm:px-3 rounded-full md:rounded-xl border transition-colors',
              age?.stale
                ? 'border-amber-300 dark:border-amber-500/35 bg-amber-50 dark:bg-amber-500/15 hover:bg-amber-100 dark:hover:bg-amber-500/20'
                : 'border-emerald-200 dark:border-emerald-500/25 bg-emerald-50 dark:bg-emerald-500/15 hover:bg-emerald-100 dark:hover:bg-emerald-500/15',
            )}
          >
            <CircleDot className={cn('size-3.5', age?.stale ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400 animate-pulse')} />
            <span className={cn('text-xs font-bold max-w-24 truncate hidden sm:inline', age?.stale ? 'text-amber-800 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-400')}>
              {currentShift.cashier_name}
            </span>
            <span className={cn('text-[12px] font-semibold uppercase tracking-wide hidden sm:inline', age?.stale ? 'text-amber-700 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400')}>
              {age?.stale ? t('shift.days', { n: age.days }) : t('shift.openShort')}
            </span>
          </button>

          {menuOpen && !isPhone && (
            <div
              role="menu"
              aria-label={t('shift.title')}
              className="animate-pop-down absolute top-11 right-0 z-50 w-[380px] max-w-[calc(100vw-2rem)] bg-popover text-popover-foreground rounded-3xl border border-border shadow-[0_24px_56px_-20px_oklch(0.2_0.03_255/0.45),0_4px_12px_-4px_oklch(0.25_0.03_255/0.1)] overflow-hidden"
            >
              {card}
            </div>
          )}

          {isPhone && (
            <BottomSheet open={menuOpen} onOpenChange={(o) => { if (!o) setMenuOpen(false) }}>
              <BottomSheetContent title={t('shift.title')}>
                <div role="menu" aria-label={t('shift.title')}>{card}</div>
              </BottomSheetContent>
            </BottomSheet>
          )}
        </div>
      ) : (
        <button
          onClick={() => setOpenDialog(true)}
          title={t('shift.open')}
          aria-label={t('shift.closedAria')}
          className="press flex items-center justify-center gap-1.5 h-9 w-9 sm:w-auto px-0 sm:px-3 rounded-full md:rounded-xl border border-amber-200 dark:border-amber-500/25 bg-amber-50 dark:bg-amber-500/10 hover:bg-amber-100 dark:hover:bg-amber-500/15 dark:bg-amber-500/15 transition-colors"
        >
          <Play className="size-3.5 text-amber-600 dark:text-amber-400" />
          <span className="text-xs font-bold text-amber-700 dark:text-amber-400 hidden sm:inline">{t('shift.closed')}</span>
        </button>
      )}

      <OpenShiftDialog open={openDialog} onClose={() => setOpenDialog(false)} />
      <CloseShiftDialog open={closeDialog} onClose={() => setCloseDialog(false)} shift={currentShift ?? null} />
      <CashMovementDialog
        open={moveDir !== null}
        direction={moveDir ?? 'out'}
        onClose={() => setMoveDir(null)}
        shift={currentShift ?? null}
      />
    </>
  )
}
